// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import Zion from './utils/Zion';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils
import { stylePremium } from './utils/helpers';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'Mar.',
    'Apr.',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

// Two series, one AAA feed each. Keep them as separate arrays so a gap in one
// (a stalled bot run, say) never gets silently interpolated using the other's dates.
const SERIES = [
  {
    key: 'regular',
    label: 'Regular gas',
    dekLabel: 'regular gas', // lowercase mid-sentence in the dek; the chart label stays title case
    color: '#F18851',
    url: 'https://ix.cnn.io/data/gas-prices/aaa_gas_prices_avg_national_five_years.json',
  },
  {
    key: 'diesel',
    label: 'Diesel',
    color: '#7C4EA5',
    url: 'https://ix.cnn.io/data/gas-prices/aaa_gas_prices_avg_national_diesel_five_years.json',
  },
];

const margins = {
  top: 35,
  right: 80,
  bottom: 25,
  left: 45,
};

// Editorial reference line for the "diesel crosses $6" story. Only draws when
// it falls within the current y-domain, so it disappears on its own once this
// isn't the point anymore (a filtered view where prices never neared $6, or a
// future rebuild once this threshold stops being news) — remove this constant
// when the story moves on rather than leaving a stale line in the chart.
const PRICE_THRESHOLD = 6;

// let recessionData;
let minValue;
let maxValue;

let minDomain;
let maxDomain;

let lastUpdatedStr;

let resizeFrame = null;
let lastDrawnWidth = null;

// Child invokes renderCallback synchronously from its constructor, well before the
// data request resolves, so draw() bails out until chartData is populated.
// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ renderCallback: draw, id: pkg.name });

// renderCallback only fires when the parent reports a new width. The listeners below
// cover width changes the parent never sees, such as an orientation flip.
// eslint-disable-next-line  no-use-before-define
window.addEventListener('resize', handleResize);
// eslint-disable-next-line  no-use-before-define
window.addEventListener('orientationchange', handleResize);

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;

stylePremium(CNNPolitics, CNNBusiness);

// Coalesce a burst of resize events into a single redraw, and skip it entirely when
// only the height changed, since the chart height is fixed.
function handleResize() {
  if (resizeFrame) {
    window.cancelAnimationFrame(resizeFrame);
  }

  resizeFrame = window.requestAnimationFrame(() => {
    resizeFrame = null;

    const node = document.querySelector('#graphic');

    if (node && node.clientWidth !== lastDrawnWidth) {
      // eslint-disable-next-line no-use-before-define
      draw();
    }
  });
}

// Dashed vertical marker with a bold date and 1-2 lines of description. Shared by
// both event annotations so mobile can swap in shorter copy without duplicating
// the halo/text boilerplate for every line.
function drawEventMarker(g, {
  className, x, chartHeight, dateLabel, lines, isMobile, isMobileSmall, align = 'right',
}) {
  const marker = g.append('g').attr('class', className);

  // Labels sit 5px higher than the plot's top edge, so the dashed line's top
  // is raised to match and still runs right up to them instead of stopping short.
  const labelLift = 5;

  marker.append('line')
    .attr('x1', x)
    .attr('x2', x)
    .attr('y1', -labelLift)
    .attr('y2', chartHeight)
    .attr('stroke', '#666')
    .attr('stroke-width', 1)
    .attr('stroke-dasharray', '4,4');

  // Label sits to the right of the line by default, but can flip to the left —
  // useful when the line falls late enough in the timeline that a right-hand
  // label would crowd the endpoint annotations.
  const labelX = align === 'left' ? x - 8 : x + 8;
  const textAnchor = align === 'left' ? 'end' : 'start';
  const allLines = [{ text: dateLabel, bold: true }, ...lines.map(text => ({ text, bold: false }))];

  const fontSize = isMobileSmall ? '11px' : '12px';
  const lineHeight = isMobileSmall ? 13 : 15;

  allLines.forEach(({ text, bold }, i) => {
    const y = 8 - labelLift + i * lineHeight;

    marker.append('text')
      .attr('x', labelX)
      .attr('y', y)
      .attr('text-anchor', textAnchor)
      .style('font-size', fontSize)
      .style('font-weight', bold ? 'bold' : 'normal')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text(text);

    marker.append('text')
      .attr('x', labelX)
      .attr('y', y)
      .attr('text-anchor', textAnchor)
      .style('font-size', fontSize)
      .style('font-weight', bold ? 'bold' : 'normal')
      .style('fill', bold ? '#333' : '#666')
      .text(text);
  });
}

function showUnavailable(reason) {
  console.error(reason);

  const container = d3.select('#graphic');
  container.html('');
  container.append('p')
    .style('text-align', 'center')
    .style('padding', '40px')
    .text('Data temporarily unavailable');

  if (window.pymChild) {
    window.pymChild.sendHeight();
  }
}

// Turn a year-ago price into the "up/down from $X.XX a year ago" fragment shared
// by both series. Reports the year-ago level rather than the delta, so the dek
// carries two readable prices per series instead of a price plus a raw change.
function yearAgoText(yearChange, yearAgoPrice) {
  if (yearChange === 0) {
    return `unchanged from <span class="highlighted-value">$${yearAgoPrice.toFixed(2)}</span> a year ago`;
  }
  const direction = yearChange > 0 ? 'up' : 'down';
  return `${direction} from <span class="highlighted-value">$${yearAgoPrice.toFixed(2)}</span> a year ago`;
}

Promise.all(SERIES.map(s => d3.json(s.url))).then((results) => {
  // Check for URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const freezeDateParam = urlParams.get('date');
  const yearsParam = urlParams.get('years');
  const yearParam = urlParams.get('year');
  let freezeDate = null;
  let yearsLimit = null;
  let specificYear = null;

  // Parse freeze date parameter (format: ?date=YYYYMMDD)
  if (freezeDateParam && freezeDateParam.length === 8) {
    const year = parseInt(freezeDateParam.substring(0, 4));
    const month = parseInt(freezeDateParam.substring(4, 6)) - 1; // Month is 0-indexed
    const day = parseInt(freezeDateParam.substring(6, 8));
    freezeDate = new Date(year, month, day);
    console.log('Freezing chart at date:', freezeDate);
  }

  // Parse years parameter (format: ?years=1 or ?years=2, etc.)
  if (yearsParam) {
    yearsLimit = parseInt(yearsParam);
    console.log('Limiting chart to', yearsLimit, 'year(s)');
  }

  // Parse year parameter (format: ?year=2026)
  if (yearParam) {
    specificYear = parseInt(yearParam);
    console.log('Showing calendar year:', specificYear);
  }

  // Process each series independently, so a gap or lag in one feed never
  // borrows dates from the other.
  SERIES.forEach((s, i) => {
    const data = results[i];

    // Map and filter data, removing any invalid entries
    let seriesData = data.data
      .map(d => ({
        date: d3.timeParse('%Y-%m-%d')(d.date),
        value: d.price === null || d.price === undefined ? NaN : +d.price,
      }))
      .filter(d => d.date && !isNaN(d.value));

    // If freeze date is set, filter data to only include dates up to freeze date
    if (freezeDate) {
      seriesData = seriesData.filter(d => d.date <= freezeDate);
    }

    // If specific year is set, filter to that calendar year
    if (specificYear) {
      const yearStart = new Date(specificYear, 0, 1);
      const yearEnd = new Date(specificYear, 11, 31, 23, 59, 59);
      seriesData = seriesData.filter(d => d.date >= yearStart && d.date <= yearEnd);
    }

    // If years limit is set, filter data to only include the specified number of years
    if (yearsLimit && yearsLimit > 0) {
      // Find the maximum date in the (possibly frozen) data
      const maxDate = d3.max(seriesData, d => d.date);
      // Calculate the minimum date by subtracting the specified years
      const minDate = new Date(maxDate);
      minDate.setFullYear(minDate.getFullYear() - yearsLimit);
      // Filter to only include data within the year range
      seriesData = seriesData.filter(d => d.date >= minDate);
    }

    s.data = seriesData;
    s.rawMetadata = data.metadata;

    if (seriesData.length === 0) {
      s.lastData = null;
      return;
    }

    const seriesMaxDate = d3.max(seriesData, d => d.date);
    const lastDataArray = seriesData.filter(d => d.date.getTime() === seriesMaxDate.getTime());
    s.lastData = lastDataArray.length > 0 ? lastDataArray[0] : seriesData[seriesData.length - 1];
  });

  // Validate we have data to display
  if (SERIES.every(s => s.data.length === 0)) {
    showUnavailable('No valid gas price data available');
    return;
  }

  const allDates = SERIES.flatMap(s => s.data.map(d => d.date));
  const allValues = SERIES.flatMap(s => s.data.map(d => d.value));

  minDomain = d3.min(allDates);
  maxDomain = d3.max(allDates);

  minValue = d3.min(allValues) * 0.80;
  maxValue = d3.max(allValues) * 1.15; // extra headroom so the top annotations don't crowd the line's peak

  // Use whichever feed's timestamp is more recent for the shared footnote.
  lastUpdatedStr = SERIES.filter(s => s.lastData).reduce((latest, s) => (
    !latest || s.lastData.date > latest.date ? { date: s.lastData.date, str: s.rawMetadata.lastUpdatedStr } : latest
  ), null).str;

  // Create editorial dek with highlighted values, one clause per series: current
  // price plus the price a year ago, rather than a day-over-day change too — two
  // series' worth of day/year deltas ran long.
  const dekClauses = SERIES.map((s) => {
    let latestPrice; let yearChange; let yearAgoPrice;

    if (freezeDate && s.lastData) {
      // Calculate values from filtered data
      latestPrice = s.lastData.value;

      const yearAgo = new Date(s.lastData.date);
      yearAgo.setFullYear(yearAgo.getFullYear() - 1);
      const yearAgoData = s.data.find(d => d.date.getTime() === yearAgo.getTime());
      yearAgoPrice = yearAgoData ? yearAgoData.value : latestPrice;
      yearChange = latestPrice - yearAgoPrice;
    } else {
      // Use metadata from API
      latestPrice = s.rawMetadata.latestValue;
      yearChange = s.rawMetadata.trends.yearChange;
      yearAgoPrice = s.rawMetadata.trends.yearChangePrevPrice;
    }

    const verb = s.key === 'regular' ? 'is' : 'costs';

    return `<span class="key ${s.key}">${s.dekLabel || s.label}</span> ${verb} <span class="highlighted-value">$${latestPrice.toFixed(2)}</span> a gallon, ${yearAgoText(yearChange, yearAgoPrice)}`;
  });

  if (freezeDate) {
    // Update last updated string to reflect freeze date
    lastUpdatedStr = d3.timeFormat('%b. %-d, %Y at 9 a.m. ET')(maxDomain);
  }

  const dekText = `The national average price for ${dekClauses[0]}. ${dekClauses[1]}.`;

  if (dekText && dekText !== '') {
    d3.select('h2.dek')
      .html(dekText);
  }

  draw();
}).catch((error) => {
  showUnavailable(error);
});


function draw() {
  if (SERIES.some(s => !s.data) || SERIES.every(s => s.data.length === 0)) {
    return;
  }

  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const isMobileSmall = initialWidth <= 350;

  lastDrawnWidth = initialWidth;
  const height = 415; // +15 vs. before, matching the added top margin, so the plot area itself stays the same size

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const xTicks = isMobile ? 2 : 5;
  const yTicks = isMobile ? 5 : 6;

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0]);

  // Calculate time span in days to determine tick strategy
  const timeSpanDays = (maxDomain - minDomain) / (1000 * 60 * 60 * 24);
  const timeSpanYears = timeSpanDays / 365;

  let xAxis;

  // If showing ~1.5 years or less, use monthly ticks (every 2-3 months)
  if (timeSpanYears <= 1.5) {
    const allMonthTicks = [];
    const currentDate = new Date(minDomain.getFullYear(), minDomain.getMonth(), 1);

    while (currentDate <= maxDomain) {
      if (currentDate >= minDomain) {
        allMonthTicks.push(new Date(currentDate));
      }
      currentDate.setMonth(currentDate.getMonth() + 1);
    }

    // Adjust tick density based on time span
    let skipInterval;
    if (timeSpanDays <= 180) {
      // 6 months or less: show all months on desktop, every other on mobile
      skipInterval = isMobile ? 2 : 1;
    } else if (timeSpanDays <= 365) {
      // 1 year or less: show every 2 months on desktop, every 4 on mobile
      skipInterval = isMobile ? 6 : 2;
    } else {
      // More than 1 year: show every 3 months on desktop, every 4 on mobile
      skipInterval = isMobile ? 4 : 3;
    }

    const monthTicks = allMonthTicks.filter((d, i) =>
      // Always include first and last tick
      i === 0 || i === allMonthTicks.length - 1 || i % skipInterval === 0);

    xAxis = d3.axisBottom()
      .scale(xScale)
      .tickValues(monthTicks)
      .tickFormat((d) => {
        // Show month and year for January, or if it's the first/last tick
        if (d.getMonth() === 0 || d.getTime() === monthTicks[0].getTime() || d.getTime() === monthTicks[monthTicks.length - 1].getTime()) {
          return d3.timeFormat("%b '%y")(d);
        }
        // Show just month for other months
        return d3.timeFormat("%b '%y")(d);
      });
  } else {
    // For longer time spans, use yearly ticks
    const yearTicks = [];
    const startYear = minDomain.getFullYear();
    const endYear = maxDomain.getFullYear();

    for (let year = startYear; year <= endYear; year++) {
      // On mobile, only include even years
      if (isMobile && year % 2 !== 0) {
        continue;
      }

      const tickDate = new Date(year, 0, 1); // January 1st
      // Only include if it's within our data range (not at the very edges)
      if (tickDate > minDomain && tickDate < maxDomain) {
        yearTicks.push(tickDate);
      }
    }

    xAxis = d3.axisBottom()
      .scale(xScale)
      .tickValues(yearTicks)
      .tickFormat(d => d3.timeFormat('%Y')(d));
  }

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .ticks(yTicks)
    .tickSize(-chartWidth)
    .tickFormat(d => `$${d3.format('.2f')(d)}`);

  const path = d3.line()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y(d => yScale(d.value));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  // Append zero line
  g.append('line')
    .attr('class', 'zero-line')
    .attr('x1', 0)
    .attr('x2', chartWidth)
    .attr('y1', yScale(minValue))
    .attr('y2', yScale(minValue))
    .attr('stroke', '#393939')
    .attr('stroke-width', 1);

  // Darker reference line at the $6 threshold diesel has crossed — see
  // PRICE_THRESHOLD above. Drawn behind the price lines, same as the other grid.
  if (PRICE_THRESHOLD > minValue && PRICE_THRESHOLD < maxValue) {
    g.append('line')
      .attr('class', 'threshold-line')
      .attr('x1', 0)
      .attr('x2', chartWidth)
      .attr('y1', yScale(PRICE_THRESHOLD))
      .attr('y2', yScale(PRICE_THRESHOLD))
      .attr('stroke', '#bcbcbc')
      .attr('stroke-width', 1);
  }

  const lineWidth = window.innerWidth < 768 ? 1.5 : 2;

  SERIES.forEach((s) => {
    g.append('g')
      .attr('class', `line line-${s.key}`)
      .append('path')
      .datum(s.data)
      .attr('stroke', s.color)
      .attr('stroke-width', lineWidth)
      .attr('fill', 'none')
      .attr('d', path);
  });

  d3.select('.grid.y .domain').remove();

  // Add annotation for Russia invades Ukraine (Feb 24, 2022) — desktop only. Two
  // war markers plus two endpoint labels is too much on a narrow board, and this
  // one is further from the current story than the Iran/Israel marker.
  const russiaUkraineDate = new Date(2022, 1, 24); // February 24, 2022

  // Only show annotation if date is within chart range
  if (true || !isMobile && russiaUkraineDate >= minDomain && russiaUkraineDate <= maxDomain) {
    drawEventMarker(g, {
      className: 'russia-annotation',
      x: xScale(russiaUkraineDate),
      chartHeight,
      isMobile,
      isMobileSmall,
      dateLabel: 'Feb. 24, 2022',
      lines: ["Russia's full-scale", 'invasion of Ukraine'],
    });
  }

  // Add annotation for US/Iran war start date (Feb 28, 2026)
  const warStartDate = new Date(2026, 1, 28); // February 28, 2026

  // Only show annotation if date is within chart range
  if (warStartDate >= minDomain && warStartDate <= maxDomain) {
    drawEventMarker(g, {
      className: 'war-annotation',
      x: xScale(warStartDate),
      chartHeight,
      isMobile,
      isMobileSmall,
      // Labeled to the left of its own line — it falls late enough in the
      // timeline that a right-hand label would crowd the endpoint annotations.
      align: 'right',
      dateLabel: isMobile ? 'Feb. 28, 2026' : 'Feb. 28, 2026',
      // Three lines of description crowds the endpoint labels on mobile, where
      // this marker sits close to the right edge; two shorter lines instead.
      lines: isMobile ? ['US/Israel', 'strike Iran'] : ['US and Israel', 'strike Iran'],
    });
  }

  const validSeries = SERIES.filter(s => s.lastData && s.lastData.date && !isNaN(s.lastData.value));

  // Endpoint dots stay on their true values. Only the name/value labels move
  // apart when the two lines end close enough together to collide.
  const LABEL_GAP = 32;

  // When true, the shared "as of" date becomes the top line, sitting right on
  // the dot's own Y, and the name/value each drop down a line to make room.
  // When false (the original layout), the name stays on the dot's Y and the
  // date is tucked in just above it instead.
  const DATE_AT_DOT_Y = true;
  const endpoints = validSeries
    .map(s => ({
      series: s,
      x: xScale(s.lastData.date),
      dotY: yScale(s.lastData.value),
    }))
    .sort((a, b) => a.dotY - b.dotY);

  endpoints.forEach((e) => { e.labelY = e.dotY; });

  if (endpoints.length === 2 && Math.abs(endpoints[0].dotY - endpoints[1].dotY) < LABEL_GAP) {
    const mid = (endpoints[0].dotY + endpoints[1].dotY) / 2;
    endpoints[0].labelY = mid - LABEL_GAP / 2;
    endpoints[1].labelY = mid + LABEL_GAP / 2;
  }

  endpoints.forEach(({
    series: s, x, dotY, labelY,
  }, i) => {
    const annotation = g.append('g')
      .attr('class', `annotation annotation-${s.key}`);

    // Live endpoint pulse — a filled disc at the dot's own radius, behind the dot,
    // animated out via the .pulse keyframes. Signals this number updates on its own.
    annotation.append('circle')
      .attr('class', 'pulse')
      .attr('cx', x)
      .attr('cy', dotY)
      .attr('r', 3)
      .attr('fill', s.color);

    annotation.append('circle')
      .attr('cx', x)
      .attr('cy', dotY)
      .attr('r', 3)
      .attr('fill', s.color)
      .attr('stroke', 'none');

    // Label sits close to the dot, nudged a pixel down so its baseline reads
    // centered on the dot rather than hugging its top edge.
    const labelDx = 7;
    const labelDy = labelY + 1;

    // A pushed-apart label still needs a thin leader back to its true dot.
    if (Math.abs(labelY - dotY) > 1) {
      annotation.append('line')
        .attr('x1', x)
        .attr('y1', dotY)
        .attr('x2', x + labelDx)
        .attr('y2', labelDy)
        .attr('stroke', s.color)
        .attr('stroke-width', 1);
    }

    const label = annotation.append('g')
      .attr('transform', `translate(${[x + labelDx, labelDy]})`);

    // Shared "as of" date, stacked onto the topmost line's own label rather than
    // floating above the chart on its own — it now reads as part of a real value
    // instead of sitting in open space near an unrelated annotation.
    const showDate = i === 0;

    // Each line below is an independent <text> with its own absolute dy from
    // the label group's origin (they don't stack cumulatively off each
    // other), so reordering is just picking different dy values per line.
    const dateDy = DATE_AT_DOT_Y ? 0 : -1.1;
    const nameDy = DATE_AT_DOT_Y && showDate ? 1.2 : 0;
    const valueDy = DATE_AT_DOT_Y && showDate ? 2.4 : 1.2;

    if (showDate) {
      label.append('text')
        .attr('class', 'annotation-date-halo')
        .attr('dy', `${dateDy}em`)
        .style('font-size', '12px')
        .style('font-weight', 'bold')
        .style('stroke', '#fff')
        .style('stroke-width', '3px')
        .style('fill', 'none')
        .text(d3.timeFormat('%b %-d')(s.lastData.date));

      label.append('text')
        .attr('class', 'annotation-date')
        .attr('dy', `${dateDy}em`)
        .style('font-size', '12px')
        .style('font-weight', 'bold')
        .text(d3.timeFormat('%b %-d')(s.lastData.date));
    }

    // Series name, in the line's color, doubles as the key
    label.append('text')
      .attr('class', 'annotation-name-halo')
      .attr('dy', `${nameDy}em`)
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text(s.label);

    label.append('text')
      .attr('class', 'annotation-name')
      .attr('dy', `${nameDy}em`)
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('fill', s.color)
      .text(s.label);

    // Value stays black — one colored word is enough to make the ID, and two
    // colored numbers at different heights are harder to compare than two black ones.
    label.append('text')
      .attr('dy', `${valueDy}em`)
      .attr('class', 'annotation-value-halo')
      .style('font-size', '12px')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text(`$${d3.format('.2f')(s.lastData.value)}`);

    label.append('text')
      .attr('dy', `${valueDy}em`)
      .attr('class', 'annotation-value')
      .style('font-size', '12px')
      .style('fill', '#262626')
      .text(`$${d3.format('.2f')(s.lastData.value)}`);
  });

  // Append the current date as a timestamp below the chart
  const currentDateString = `Note: Data last updated ${lastUpdatedStr}.`;

  const timestamp = container.append('div')
    .attr('class', 'footnote')
    .text(currentDateString);

  window.pymChild.sendHeight();
}
